import React, { useState } from 'react';
import { Box } from '@mui/material';
import Typography from '@mui/material/Typography';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import {
    Dropzone,
    FileMosaic,
    FullScreen,
    ImagePreview,
} from '@dropzone-ui/react';
import { useAuthContext } from '../../../../contexts/authContext';

type Props = {
    name: string;
    title?: string;
    subtitle?: string;
    accept?: string | undefined;
    maxFiles?: number | undefined;
    onChange: (event: any) => void;
    icon?: false | boolean;
    disabled?: boolean;
    maxSizeMB?: number;
};

const DropZoneField = ({
    name,
    title = 'Arrastre y suelte algunos archivos aquí,',
    subtitle = 'o haga clic para seleccionar archivos',
    accept,
    maxFiles,
    onChange,
    icon,
    maxSizeMB,
}: Props) => {
    //onChange Formik
    //The variable dropzoneFieldFormik must be equal to the variable name of the formik
    //onChange={(value) => formik.setFieldValue('dropzoneFieldFormik', value)}

    const { modeTheme } = useAuthContext();
    const [files, setFiles] = useState<{ id: number; file: any }[]>([]);
    const [imageSrc, setImageSrc] = useState<any>(undefined);

    const updateFiles = (incommingFiles: any) => {
        const MAX_SIZE = (maxSizeMB ?? 30) * 1024 * 1024;

        const validFiles: any[] = [];

        if (incommingFiles.length > 0) {
            for (let i = 0; i < incommingFiles.length; i++) {
                const { file } = incommingFiles[i];

                if (file.size > MAX_SIZE) {
                    alert(
                        `El archivo "${
                            file.name
                        }" supera el tamaño máximo permitido de ${
                            maxSizeMB ?? 30
                        }MB`
                    );
                    continue;
                }

                validFiles.push(incommingFiles[i]);
            }
        }

        setFiles(validFiles);

        let filesObjects: any[] = [];

        if (validFiles.length > 0) {
            for (let i = 0; i < validFiles.length; i++) {
                const { file } = validFiles[i];
                const formdata = new FormData();
                formdata.append('file', file);
                filesObjects = filesObjects.concat(formdata);
            }
        }
        onChange(filesObjects);
    };

    const removeFile = (id: any) => {
        setFiles(files.filter((x) => x.id !== id));
        onChange(files.filter((x) => x.id !== id));
    };

    const handleSee = (imageSource: any) => {
        setImageSrc(imageSource);
    };

    return (
        <>
            <Dropzone
                name={name}
                onChange={updateFiles}
                value={files}
                autoClean={true}
                accept={accept}
                maxFiles={maxFiles}
                localization="ES-es"
                footer={false}
                header={files.length > 0}
            >
                {files.length === 0 && (
                    <Box
                        mt={2}
                        component="span"
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        sx={{
                            color:
                                modeTheme === 'darkMode'
                                    ? 'primary.contrastText'
                                    : 'primary.main',
                        }}
                    >
                        <Typography component="span">
                            <strong>{title}</strong>
                        </Typography>
                        <Typography component="span">
                            <strong>{subtitle}</strong>
                        </Typography>
                        <Box mt={1}>
                            <Typography component="span">
                                <>
                                    Tipos de archivos aceptados: <br />
                                    {!accept ? (
                                        <strong>
                                            .png, .jpg, .jpeg, .txt, .csv, .doc,
                                            .docx, .xls, .xlsx, .pdf, <br />{' '}
                                            .video, .audio, .rar, .zip
                                        </strong>
                                    ) : (
                                        <strong>{accept}</strong>
                                    )}
                                    <br />
                                    <strong>
                                        Tamaño máximo permitido:{' '}
                                        {maxSizeMB ?? 30}MB
                                    </strong>
                                </>
                            </Typography>
                        </Box>
                        {icon && (
                            <Box mt={1}>
                                <CloudUploadOutlinedIcon
                                    sx={{
                                        color:
                                            modeTheme === 'darkMode'
                                                ? 'primary.contrastText'
                                                : 'primary.main',
                                        fontSize: '3.5rem',
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                )}
                {files.length > 0 &&
                    files.map((file: any) => (
                        <FileMosaic
                            {...file}
                            onDelete={removeFile}
                            key={file.id}
                            info
                            preview
                            resultOnTooltip
                            alwaysActive
                            onSee={handleSee}
                        />
                    ))}
            </Dropzone>
            <FullScreen
                open={imageSrc !== undefined}
                onClose={() => setImageSrc(undefined)}
            >
                <ImagePreview src={imageSrc} />
            </FullScreen>
        </>
    );
};

export default DropZoneField;
